/**
 * ImportWizard — CSV import in five steps: upload, map columns, check rows, review, done.
 * @layer widget
 * @package @plerity/dashboard-shared
 *
 * Drop it in a page or inside a Modal. It owns no data access: the app passes a `CsvImportTarget` with a
 * field list, a zod-compatible row schema and a `commit(rows, { dryRun })` function. Every row is validated
 * in the browser (streamed, so a 50 MB file is fine), the server is asked to dry-run the batch, and only
 * then does the user confirm the real import. Problem rows are always listed with their spreadsheet row
 * number, never dropped silently, and skipping them needs an explicit tick.
 *
 * Reuse:   CRM importer and the social bulk dialog plug in through `CsvImportTarget` (see the package README).
 * Upsert:  opt in with `target.upsert` (`ImportUpsertCapability`). The dry run (or `upsert.findDuplicates`)
 *          reports per-row duplicate matches, the review step lets the user pick create / update / skip per
 *          row (default `skip`), and the real `commit()` receives them as `options.decisions`. Without
 *          `upsert` nothing changes: `commit()` gets exactly `{ dryRun }`.
 * Parsing: `@plerity/csv` (streaming RFC-4180, BOM/encoding aware, formula-injection flagging).
 *
 * @example
 * ```tsx
 * <ImportWizard
 *   target={{
 *     entityName: "contacts",
 *     fields: [{ key: "email", label: "Email", required: true }],
 *     schema: z.object({ email: z.string().email() }),
 *     commit: (rows, { dryRun }) => api.importContacts(rows.map((r) => r.data), { dryRun }),
 *   }}
 *   onComplete={(summary) => toast.success(`Imported ${summary.created} contacts`)}
 * />
 * ```
 */

import { useEffect, useId, useRef } from "react";
import { IMPORT_STEPS, type ImportStep, type ImportWizardProps } from "./ImportWizard.types";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  DoneStep,
  MapStep,
  ReviewStep,
  UploadStep,
  ValidateStep,
} from "./ImportWizardSteps";
import { DEFAULT_MAX_FILE_BYTES, useCsvImport } from "./useCsvImport";

const n = (value: number): string => value.toLocaleString("en-US");

export function ImportWizard<T>({
  target,
  title,
  batchSize,
  maxRows,
  maxFileBytes = DEFAULT_MAX_FILE_BYTES,
  allowSkipInvalid,
  formulaPolicy,
  onComplete,
  onCancel,
  helpContext = "csv-import-wizard",
  className = "",
}: ImportWizardProps<T>) {
  const c = useCsvImport(target, { batchSize, maxRows, maxFileBytes, allowSkipInvalid, formulaPolicy, onComplete });
  const { state } = c;
  const titleId = useId();
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const previousStep = useRef<ImportStep>(state.step);

  // Move focus to the new step's heading so keyboard and screen-reader users land on the new content.
  useEffect(() => {
    if (previousStep.current !== state.step) {
      previousStep.current = state.step;
      stepHeadingRef.current?.focus();
    }
  }, [state.step]);

  const stepIndex = IMPORT_STEPS.findIndex((s) => s.id === state.step);
  const stepLabel = IMPORT_STEPS[stepIndex]?.label ?? "";
  const result = state.validate.result;

  const announcement = (() => {
    switch (state.step) {
      case "map":
        return `Step 2 of 5. Match the columns of ${state.file?.name ?? "your file"}.`;
      case "validate":
        if (state.validate.status === "running") return "Checking every row.";
        if (state.validate.status === "error") return "The rows could not be checked.";
        return result ? `${n(result.validCount)} rows are ready. ${n(result.errorCount)} rows have problems.` : "";
      case "review":
        if (state.commit.status === "running") return "Saving rows.";
        if (state.dryRun.status === "running") return "Checking the rows with the server.";
        if (state.dryRun.status === "error" || state.commit.status === "error")
          return "A step failed. Retry is available.";
        return `The server check finished. ${n(c.writeCount)} rows are ready to import.`;
      case "done":
        return "The import finished.";
      default:
        return "";
    }
  })();

  const validating = state.validate.status === "running";
  const dryRunning = state.dryRun.status === "running";
  const committing = state.commit.status === "running";
  const heading = title ?? `Import ${target.entityName} from CSV`;

  return (
    <section
      aria-labelledby={titleId}
      data-help-context={helpContext}
      className={`rounded-lg border border-gray-200 bg-white text-gray-900 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 ${className}`}
    >
      <header className="space-y-3 border-b border-gray-200 px-6 py-4 dark:border-gray-700">
        <div>
          <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {heading}
          </h2>
          {state.file && <p className="text-xs text-gray-600 dark:text-gray-400">{state.file.name}</p>}
        </div>
        <ol aria-label="Import progress" className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {IMPORT_STEPS.map((step, index) => {
            const active = index === stepIndex;
            const complete = index < stepIndex;
            return (
              <li
                key={step.id}
                aria-current={active ? "step" : undefined}
                className={
                  active
                    ? "font-semibold text-blue-700 dark:text-blue-300"
                    : complete
                      ? "text-gray-700 dark:text-gray-300"
                      : "text-gray-600 dark:text-gray-400"
                }
              >
                <span aria-hidden="true">{complete ? "✓ " : `${index + 1}. `}</span>
                {step.label}
                {complete && <span className="sr-only"> (completed)</span>}
              </li>
            );
          })}
        </ol>
      </header>

      <output className="sr-only">{announcement}</output>

      <div className="space-y-4 px-6 py-5">
        <h3
          ref={stepHeadingRef}
          tabIndex={-1}
          className="text-base font-semibold text-gray-900 focus:outline-none dark:text-gray-100"
        >
          {stepLabel}
        </h3>

        {state.step === "upload" && <UploadStep c={c} target={target} maxFileBytes={maxFileBytes} />}
        {state.step === "map" && <MapStep c={c} target={target} />}
        {state.step === "validate" && <ValidateStep c={c} target={target} />}
        {state.step === "review" && <ReviewStep c={c} target={target} />}
        {state.step === "done" && <DoneStep c={c} target={target} />}
      </div>

      <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
        {state.step === "upload" && (
          <>
            {onCancel ? (
              <button type="button" className={BUTTON_SECONDARY} onClick={onCancel}>
                Cancel
              </button>
            ) : (
              <span />
            )}
            <span />
          </>
        )}

        {state.step === "map" && (
          <>
            <button type="button" className={BUTTON_SECONDARY} onClick={c.back}>
              Choose another file
            </button>
            <button
              type="button"
              className={BUTTON_PRIMARY}
              disabled={!c.canCheckRows}
              onClick={() => void c.validate()}
            >
              Check rows
            </button>
          </>
        )}

        {state.step === "validate" && (
          <>
            {validating ? (
              <button type="button" className={BUTTON_SECONDARY} onClick={c.cancelValidate}>
                Cancel
              </button>
            ) : (
              <button type="button" className={BUTTON_SECONDARY} onClick={c.back}>
                Back to columns
              </button>
            )}
            {state.validate.status === "done" && result && (
              <button
                type="button"
                className={BUTTON_PRIMARY}
                disabled={result.validCount === 0}
                onClick={() => void c.runDryRun()}
              >
                Check with the server
              </button>
            )}
          </>
        )}

        {state.step === "review" && (
          <>
            <button type="button" className={BUTTON_SECONDARY} disabled={dryRunning || committing} onClick={c.back}>
              Back to rows
            </button>
            {state.dryRun.status === "done" && state.commit.status !== "error" && (
              <button
                type="button"
                className={BUTTON_PRIMARY}
                disabled={!c.canCommit || committing}
                onClick={() => void c.runCommit()}
              >
                {committing ? "Importing..." : `Import ${n(c.writeCount)} row${c.writeCount === 1 ? "" : "s"}`}
              </button>
            )}
          </>
        )}

        {state.step === "done" && (
          <>
            <button type="button" className={BUTTON_SECONDARY} onClick={c.reset}>
              Import another file
            </button>
            {onCancel && (
              <button type="button" className={BUTTON_PRIMARY} onClick={onCancel}>
                Close
              </button>
            )}
          </>
        )}
      </footer>
    </section>
  );
}
